import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../../liberis-component-library';
import useLocale from '../../hooks/useLocale';
import { Routes } from '../../models/AppTypes';
import MissingDataExplainer from './MissingDataExplainer';
import MissingDataInput from './MissingDataInput';
import style from './MissingDataPage.scss';
import useCapitalAccountData from '../useCapitalAccountData';
import { isMissingData } from '../utils';

const LoadingStep = 0;
const ExplainerStep = 1;
const InputStep = 2;

export default function MissingDataPage(): React.JSX.Element {
  const [isCapitalAccount, capitalAccountData, updateData] = useCapitalAccountData();
  const [step, setStep] = useState(LoadingStep);
  const { getLocale } = useLocale();
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSave = async (ownership: number, ssn: string | undefined) => {
    setStep(LoadingStep);
    const success = await updateData({
      ...capitalAccountData,
      signer: {
        ownership: ownership,
        ssn: ssn,
      },
    });
    
    if (success) {
      console.log('MissingDataPage: data saved successfully');
      navigate(Routes.root);
    } else {
      setStep(InputStep);
    }
  };

  useEffect(() => {
    if (!isCapitalAccount) {
      console.log('MissingDataPage: not capital account');
      navigate(Routes.root);
      return;
    }
    if (step !== LoadingStep) {
      console.log('MissingDataPage: already loaded');
      return;
    }
    if (!capitalAccountData) {
      console.log('MissingDataPage: fetching capital account data');
      return;
    }

    if (isMissingData(capitalAccountData)) {
      // If data is missing then start the input flow
      console.log('MissingDataPage: data requires completion');
      setStep(ExplainerStep);
    } else {
      // If we already have data then skip to the contract view
      console.log('MissingDataPage: data is complete');
      navigate(Routes.root);
    }
  }, [capitalAccountData]);

  switch (step) {
    case LoadingStep:
      return (
        <div aria-busy='true' aria-live='polite' aria-label={getLocale('Loading')} tabIndex={-1}>
          <Loading className={style.loader} isLoading/>
        </div>
      );

    case ExplainerStep:
      return <MissingDataExplainer onContinue={() => setStep(InputStep)} />;

    case InputStep:
      return <MissingDataInput 
        business={capitalAccountData}
        availableOwnership={100}
        onSave={onSave}
        />;
  }
}